import { Button, Heading, Alert } from '@mm/components';
import { useSessionStorage } from '@mm/hooks';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import {
  selectCurrentUser,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import {
  activeChatbotIdSelector,
  activeCompanySelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { CSSProperties, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { CHATBOT_EDITOR } from '../../../../../../common/constants/routes';
import { usePhoneNumber } from '../../../../../../common/hooks/use-phone-number';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { ChannelIcon } from '../../../../liveChat/components/ChannelIcon';
import { useChannelConnection } from '../../Automations/AutomationEdit/InputTriggers/use-channel-connection';
import { useConnectNativeIntegration } from '../../Integrations/NativeIntegrations/use-connect-native-integration';
import {
  EDUCATIONAL_CARD_DEFINITIONS,
  EDUCATIONAL_CARD_TYPES,
  EducationalCardDefinition,
  EducationalCards,
} from './EducationalCardDefinitions';
import styles from './EducationalCardsBlock.module.css';
import classNames from 'classnames';
import { getAutomationsAggregateStats } from '@mm/state/selectors/automations';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { XRayGrid } from '../../Integrations/NativeIntegrations/components/XRayIngegrations/XRayIntegrations';
import { XRayIcon } from '@mm/icons';
import xrayTable from 'assets/xray-table.png';
import { selectAccountDetails } from '@mm/state/selectors/subscriptionSelectors';
import { useFreeTrialUpgradeModal } from '../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { landmineNames } from '@mm/constants/landmine-names';

type Props = {
  card: EducationalCardDefinition;
  onClick: () => void;
  setDimissiedCards: (value: string[]) => void;
  dismissedCards: string[];
};

const EducationalCard = (props: Props) => {
  const { card, onClick, setDimissiedCards, dismissedCards } = props;
  return (
    <div
      className={styles.card}
      style={
        {
          gridColumnStart: card.columnSpan ? `span ${card.columnSpan}` : '1',
        } as CSSProperties
      }
    >
      <img src={card.backgroundImage} className={styles.backgroundImage} />
      {card?.channel && (
        <ChannelIcon channel={card.channel} className={styles.icon} />
      )}
      {card?.icon && <img src={card?.icon} className={styles.icon} />}
      <div className={styles.cardDescriptionBlock}>
        <div className={styles.cardHeadingAndDescription}>
          <Heading size={3} className={styles.cardHeading}>
            {card.heading}
          </Heading>

          <div
            className={classNames(styles.cardHeadingContent, {
              [styles.withImage]: !!card.descriptionImage,
            })}
          >
            {card.descriptionImage && (
              <img
                src={card.descriptionImage}
                className={styles.descriptionImage}
              />
            )}

            <div>
              {card.subheading && (
                <Heading size={4} className={styles.cardHeading}>
                  {card.subheading}
                </Heading>
              )}

              <div className={styles.description}>{card.description}</div>
            </div>
          </div>
        </div>
        <div className={styles.buttonsWrapper}>
          {card?.dismissable && (
            <PrimaryButton
              className={styles.dismissButton}
              color="transparent-no-shadow"
              onClick={() =>
                setDimissiedCards([...dismissedCards, `${card.cardType}`])
              }
            >
              Dismiss
            </PrimaryButton>
          )}
          <PrimaryButton className={styles.actionButton} onClick={onClick}>
            {card.button}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export const EducationalCardsBlock = () => {
  const {
    isSMSConnected,
    isInstagramConnected,
    isFacebookConnected,
    isEmailConnected,
    connectChannel,
  } = useChannelConnection(() => {});
  const modalActions = useModalActions();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const company = useSelector(activeCompanySelector);
  const activeFacebookPage = useSelector(selectActiveFacebookPage);
  const currentUser = useSelector(selectCurrentUser);
  const { needsToVerifyEmail, userAttributes } = useSelector(
    selectSessionSelector,
  );
  const firstChatbotId = useSelector(
    (state: StoreState) => state.chatbot.basicListByCompany[0]?.id,
  );
  const accountDetails = useSelector(selectAccountDetails);
  const botId = chatbotId || firstChatbotId;
  const history = useHistory();
  const { isSmsSupported } = usePhoneNumber();
  const { connectNativeIntegration } = useConnectNativeIntegration();

  const sessionStorageKeyName = `educationalCardsBlockHideState-${activeFacebookPage?.id}`;
  const [hiddenEducationalCards, setHideEducationalCards] = useSessionStorage(
    sessionStorageKeyName,
    [],
  );

  const aggregateStats = useSelector(getAutomationsAggregateStats);

  const getEmailPromoters = useSelector((state: StoreState) =>
    (state.promoter.entities || [])
      .filter(p => p?.botId?.toString() === botId?.toString())
      .filter(p => p.type === 'getemails'),
  );

  const redirect = useCallback(
    (path: string) => {
      if (botId) {
        history.push(path);
      }
    },
    [botId, history],
  );

  const cards: EducationalCardDefinition[] = useMemo(() => {
    const hideConnectedChannels = (cardType: EducationalCards) => {
      switch (cardType) {
        case 'xray':
          return false; // Note: We have a different approach for X-Ray currently
        // case 'instagram':
        //   return !isInstagramConnected;
        // case 'facebook':
        //   return !isFacebookConnected;
        // case 'sms':
        //   return isSmsSupported && !isSMSConnected;
        case 'email':
          return !isEmailConnected;
        // case 'download_app':
        //   return !userAttributes?.hasMobile;
        case 'create_automation':
          return !aggregateStats.automationsCount;
        case 'billingAddress':
          return (
            company?.requiresAddressVerification &&
            accountDetails?.account?.editBillingLink
          );
        case 'visit_inbox':
          return (
            aggregateStats.automationsCount &&
            isEmailConnected &&
            getEmailPromoters.length
          );
        // case 'confirm_email':
        //   return needsToVerifyEmail;
        // case 'connect_google_sheets':
        //   return !currentUser?.googleSheetsActivated;
        // case 'connect_mailchimp':
        //   return !currentUser?.mailchimpActivated;
        default:
          return true;
      }
    };

    const visibleCards = EDUCATIONAL_CARD_TYPES.filter(hideConnectedChannels)
      .filter(cardType => !hiddenEducationalCards.includes(cardType))
      .map(cardType => ({
        ...EDUCATIONAL_CARD_DEFINITIONS[cardType],
        cardType,
      }));

    return visibleCards.sort((a, b) => a.priority - b.priority);
  }, [
    isFacebookConnected,
    isInstagramConnected,
    isSMSConnected,
    isEmailConnected,
    isSmsSupported,
    hiddenEducationalCards,
    needsToVerifyEmail,
    userAttributes,
    currentUser,
    aggregateStats.automationsCount,
    getEmailPromoters.length,
  ]);

  const onTriggerClick = useCallback(
    (educatonalCard: EducationalCardDefinition) => {
      switch (educatonalCard.cardType) {
        case 'facebook':
          connectChannel('facebook');
          break;
        case 'instagram':
          connectChannel('instagram');
          break;
        case 'sms':
          connectChannel('sms');
          break;
        case 'email':
          connectChannel('email');
          break;
        case 'create_automation':
          redirect(
            BotBuilderRoutes.automationNew.getLink({
              chatbotId: botId,
            }),
          );
          break;
        case 'xray':
          redirect(
            BotBuilderRoutes.xRay.getLink({
              chatbotId: botId,
            }),
          );
          break;
        case 'visit_inbox':
          redirect(`${CHATBOT_EDITOR}/${botId}/live-chat`);
          break;
        case 'download_app':
          modalActions.openMobileDownloadModal();
          break;
        case 'confirm_email':
          modalActions.openVerifyEmailModal();
          break;
        case 'connect_google_sheets':
          connectNativeIntegration('googleSheets');
          break;
        case 'connect_mailchimp':
          connectNativeIntegration('mailchimp');
          break;
        case 'billingAddress':
          setHideEducationalCards([
            ...hiddenEducationalCards,
            educatonalCard.cardType,
          ]);
          window.open(accountDetails?.account?.editBillingLink, '_blank');
          break;
        default:
          break;
      }
    },
    [connectChannel, modalActions, redirect, botId, connectNativeIntegration],
  );

  const freeTrialUpgradeModal = useFreeTrialUpgradeModal();

  const showTrialEndAlert =
    freeTrialUpgradeModal.isFreePlan ||
    (freeTrialUpgradeModal.isFreeTrial &&
      (freeTrialUpgradeModal.isExpired ||
        freeTrialUpgradeModal.isSendLimitReached));

  const promotersCRUDACtions = usePromoterCRUDActions();

  useEffect(() => {
    // pagination: {page: 3, perPage: 3 }
    promotersCRUDACtions.retrieveAll(botId, 'getemails');
  }, []);

  return (
    <div className={styles.container}>
      <Heading size={2} className={styles.heading}>
        Capture, qualify, and close leads automatically in 15 minutes.
      </Heading>

      {getEmailPromoters.length && activeFacebookPage?.getemailsActivated ? (
        <div className={classNames(styles.card, styles.xray)}>
          {showTrialEndAlert && (
            <Alert
              css={{
                container: styles.trialEndAlert,
              }}
              borderRadius={0}
              showCloseIcon={false}
              type="warning"
              showHeader
            >
              <div>
                <div>
                  <span className={styles.trialEndAlertTitle}>
                    Leads are not being captured
                  </span>
                  <span className={styles.trialEndAlertSubtitle}>
                    Please upgrade your plan to unlock all features and continue
                    capturing leads
                  </span>
                </div>

                <Button
                  onClick={e => {
                    e?.preventDefault();
                    freeTrialUpgradeModal.openFreeTrialUpgradeModal(
                      {
                        landmineName: landmineNames.XRAY_DASHBOARD,
                      },
                      e,
                    );
                  }}
                >
                  Upgrade Plan
                </Button>
              </div>
            </Alert>
          )}

          <Heading size={3} className={styles.cardHeading}>
            X-Ray Pixel Triggers
          </Heading>

          <XRayGrid botId={botId} className={styles.table} cap={3} />

          {getEmailPromoters?.length > 3 && (
            <Button
              color="link"
              onClick={() =>
                redirect(
                  BotBuilderRoutes.xRay.getLink({
                    chatbotId: botId,
                  }),
                )
              }
            >
              Go to All Triggers ({getEmailPromoters.length})
            </Button>
          )}
        </div>
      ) : activeFacebookPage?.getemailsActivated ? null : (
        <div className={classNames(styles.card, styles.xray)}>
          <div className={styles.xrayCardHeaderRow}>
            <XRayIcon color="#AD6CD6" className={styles.xrayIcon} />
            <div>
              <div className={styles.xrayStepHeader}> Step 1.</div>
              <Heading size={3} className={styles.cardHeading}>
                Install X-Ray Pixel
              </Heading>
            </div>
          </div>
          <div className={styles.xrayInfoRow}>
            <div className={styles.xrayTableImage}>
              <img src={xrayTable} alt="X-Ray Pixel Results Table" />
            </div>
            <div className={styles.xrayTextColumn}>
              <div className={styles.xrayTextHeading}>
                X-Ray Pixel captures leads from your anonymous web traffic
              </div>
              <div>
                Install the script & capture leads without coupons, popups, or
                widgets.
              </div>
              <div>Automatic anonymous lead capture.</div>
            </div>
          </div>
          <div className={styles.cardDescriptionBlock}>
            <div className={styles.buttonsWrapper}>
              <PrimaryButton
                className={classNames(
                  styles.actionButton,
                  styles.actionButtonXray,
                )}
                onClick={() =>
                  redirect(
                    BotBuilderRoutes.xRay.getLink({
                      chatbotId: botId,
                    }),
                  )
                }
              >
                Install X-Ray Pixel
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      <div className={styles.cardsBlock}>
        {cards.map(
          (card, index) =>
            index < 4 && (
              <EducationalCard
                card={{
                  ...card,
                  columnSpan: !index || cards.length === 2 ? 2 : 1,
                }}
                key={card.heading}
                onClick={() => onTriggerClick(card)}
                setDimissiedCards={setHideEducationalCards}
                dismissedCards={hiddenEducationalCards}
              />
            ),
        )}
      </div>
    </div>
  );
};
